import type { TFunction } from 'react-i18next'
import * as Yup from 'yup'

import type {
  FormQuestionType,
  ValidationRule
} from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import type { ValidationRuleFormValue } from './addQuestionModal/AddQuestionModal.types'

export const DEFAULT_LOCALE = 'en-US' as const

type Option<T extends string = string> = { label: string; value: T }

export const getContactTypeOptions = (t: TFunction): Option[] => [
  { label: t('grants.forms.questions.contactType.LEGAL_SIGNATORY'), value: 'LEGAL_SIGNATORY' },
  { label: t('grants.forms.questions.contactType.COMMS_PR_LEAD'), value: 'COMMS_PR_LEAD' },
  { label: t('grants.forms.questions.contactType.FINANCE_LEAD'), value: 'FINANCE_LEAD' },
  { label: t('grants.forms.questions.contactType.EXECUTIVE_LEAD'), value: 'EXECUTIVE_LEAD' }
]

export const getNameTypeOptions = (t: TFunction): Option[] => [
  { label: t('grants.forms.questions.nameType.first'), value: 'first' },
  { label: t('grants.forms.questions.nameType.last'), value: 'last' }
]

export const getQuestionTypeOptions = (t: TFunction): Option<FormQuestionType>[] => [
  { label: t('grants.forms.questions.questionType.text'), value: 'text' },
  { label: t('grants.forms.questions.questionType.textarea'), value: 'textarea' },
  { label: t('grants.forms.questions.questionType.number'), value: 'number' },
  { label: t('grants.forms.questions.questionType.email'), value: 'email' },
  { label: t('grants.forms.questions.questionType.name'), value: 'name' },
  { label: t('grants.forms.questions.questionType.score'), value: 'score' },
  { label: t('grants.forms.questions.questionType.award_amount'), value: 'award_amount' },
  { label: t('grants.forms.questions.questionType.final_award_amount'), value: 'final_award_amount' },
  { label: t('grants.forms.questions.questionType.documents'), value: 'documents' },
  { label: t('grants.forms.questions.questionType.decision'), value: 'decision' },
  { label: t('grants.forms.questions.questionType.select'), value: 'select' },
  { label: t('grants.forms.questions.questionType.award_recommendation'), value: 'award_recommendation' },
  { label: t('grants.forms.questions.questionType.website'), value: 'website' },
  { label: t('grants.forms.questions.questionType.country_search'), value: 'country_search' },
  { label: t('grants.forms.questions.questionType.registry_search'), value: 'registry_search' },
  { label: t('grants.forms.questions.questionType.organisation_search'), value: 'organisation_search' },
  { label: t('grants.forms.questions.questionType.date'), value: 'date' }
]

export const getQuestionTypeLabel = (t: TFunction, type: FormQuestionType): string =>
  t(`grants.forms.questions.questionType.${type}`)

// 'text' remains for backwards compatibility with legacy questions that used text fields for numeric input
export const TYPES_WITH_VALIDATION_RULES: FormQuestionType[] = [
  'text',
  'number',
  'award_amount',
  'final_award_amount',
  'award_recommendation'
]

export const CANDIDATE_FIELD_OPTIONS: Option[] = [
  { label: 'Award', value: 'award' },
  { label: 'Award Recommendation', value: 'awardRecommendation' },
  { label: 'Final Award Amount', value: 'finalAwardAmount' },
  { label: 'Operating Budget', value: 'operatingBudget' }
]

export const COMPARISON_OPERATOR_OPTIONS: Option[] = [
  { label: '< (less than)', value: '<' },
  { label: '<= (less than or equal)', value: '<=' },
  { label: '> (greater than)', value: '>' },
  { label: '>= (greater than or equal)', value: '>=' },
  { label: '= (equal)', value: '=' }
]

export const VALIDATION_RULE_TYPE_OPTIONS: Option<ValidationRule['type']>[] = [
  { label: 'Candidate field', value: 'candidate_field_comparison' },
  { label: 'Fixed value', value: 'value_comparison' }
]

export const validationRulesYupSchema = (t: TFunction) =>
  Yup.array().of(
    Yup.object().shape({
      type: Yup.string().oneOf(['candidate_field_comparison', 'value_comparison']).required(t('errorMessage.required')),
      candidateField: Yup.string().when('type', {
        is: 'candidate_field_comparison',
        then: schema => schema.required(t('errorMessage.required')),
        otherwise: schema => schema.optional()
      }),
      value: Yup.number()
        .transform((value, original) => (original === undefined || original === '' ? undefined : value))
        .when('type', {
          is: 'value_comparison',
          then: schema => schema.required(t('errorMessage.required')),
          otherwise: schema => schema.optional()
        }),
      operator: Yup.string().required(t('errorMessage.required')),
      percentage: Yup.number()
        .transform((value, original) => (original === undefined || original === '' ? undefined : value))
        .optional()
        .positive(t('errorMessage.mustBePositiveNumber')),
      errorMessage: Yup.string().optional()
    })
  )

export function buildValidationRulesPayload(
  rules: ValidationRuleFormValue[],
  questionType: FormQuestionType
): { validationRules?: ValidationRule[] } {
  if (!TYPES_WITH_VALIDATION_RULES.includes(questionType)) return {}

  const validationRules = rules
    .filter(
      r =>
        r.operator &&
        ((r.type === 'candidate_field_comparison' && r.candidateField) ||
          (r.type === 'value_comparison' && r.value !== ''))
    )
    .map(r => {
      if (r.type === 'value_comparison') {
        return {
          type: 'value_comparison' as const,
          value: Number(r.value),
          operator: r.operator as ValidationRule['operator'],
          ...(r.errorMessage ? { errorMessage: r.errorMessage } : {})
        }
      }

      return {
        type: 'candidate_field_comparison' as const,
        candidateField: r.candidateField,
        operator: r.operator as ValidationRule['operator'],
        ...(r.percentage !== undefined ? { percentage: Number(r.percentage) } : {}),
        ...(r.errorMessage ? { errorMessage: r.errorMessage } : {})
      }
    })

  return { validationRules }
}
